// ADV-P0I — compact Provider Stack / API Usage tile.
// Renders provider health, Tier1/Tier2 effectiveness, and Brave cost-cap status
// in plain English. No banner. No raw JSON. No API key. Read-only.
import React, { useEffect, useState } from 'react';
import { Layers } from 'lucide-react';
import { fetchProviderStackHealth, type ProviderStackHealth } from '../lib/providerStackClient';

function pct(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '--';
  return `${Math.round(v * 100)}%`;
}

function money(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '--';
  return `$${v.toFixed(2)}`;
}

function _Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  const color = tone === 'good' ? 'text-emerald-600'
    : tone === 'warn' ? 'text-amber-600'
    : tone === 'bad' ? 'text-rose-600'
    : 'text-[var(--app-text)]';
  return (
    <div className="flex flex-col">
      <span className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted-text)]">{label}</span>
      <span className={`text-xs font-black ${color}`}>{value}</span>
    </div>
  );
}

export default function ProviderStackTile() {
  const [data, setData] = useState<ProviderStackHealth | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'unavailable'>('loading');

  useEffect(() => {
    let alive = true;
    fetchProviderStackHealth().then((d) => {
      if (!alive) return;
      if (d) { setData(d); setState('ok'); } else { setState('unavailable'); }
    });
    return () => { alive = false; };
  }, []);

  if (state === 'loading') {
    return (
      <div data-testid="provider-stack-tile-loading" className="rounded-xl border border-[var(--card-border)] bg-[var(--card-bg)] px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
        Provider Stack &middot; loading&hellip;
      </div>
    );
  }
  if (state === 'unavailable' || !data) {
    return (
      <div data-testid="provider-stack-tile-unavailable" className="rounded-xl border border-dashed border-[var(--card-border)] bg-[var(--card-bg)] px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
        Provider Stack &middot; status unavailable
      </div>
    );
  }

  const cap = data.brave_monthly_cap_usd ?? 10;
  const remaining = data.brave_cap_remaining_estimate ?? cap;
  let budgetLabel = 'Within budget';
  let budgetTone = 'good';
  if (remaining <= 0) { budgetLabel = 'Tier 2 capped — using cached/structured sources'; budgetTone = 'bad'; }
  else if (remaining <= cap * 0.2) { budgetLabel = 'Near cap'; budgetTone = 'warn'; }

  const t1 = data.tier1_hit_rate ?? 0;
  const tier1Tone = t1 < 0.3 ? 'warn' : 'good';
  const tier1Note = t1 < 0.3 ? 'Needs more Tier 1 coverage' : 'Tier 1 healthy';

  const calls = data.tier2_brave_called_count ?? 0;
  const hits = data.tier2_cache_hit_count ?? 0;
  const cacheRate = (calls + hits) > 0 ? hits / (calls + hits) : null;

  const tier2 = (data.active_tier2_provider || 'none');
  const tier2Label = tier2 === 'brave' ? 'Brave' : (tier2 === 'tavily' ? 'Tavily (bridge)' : (tier2 === 'exa' ? 'EXA' : 'None'));

  return (
    <div data-testid="provider-stack-tile" className="rounded-xl border border-[var(--card-border)] bg-[var(--card-bg)] p-3 space-y-2">
      <div className="flex items-center gap-1.5">
        <Layers size={12} className="text-[var(--muted-text)]" />
        <span className="text-[10px] font-black uppercase tracking-widest text-[var(--app-text)]">Provider Stack</span>
        <span data-testid="provider-stack-tier2" className="ml-auto text-[9px] font-black uppercase tracking-widest text-emerald-600">
          Tier 2: {tier2Label}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-x-3 gap-y-2">
        <_Stat label="Tier 1" value={`${data.fmp_configured ? 'FMP' : ''}${data.fmp_configured && data.sec_enabled ? ' / ' : ''}${data.sec_enabled ? 'SEC' : ''}` || 'none'} />
        <_Stat label="Tier 1 hit rate" value={pct(data.tier1_hit_rate)} tone={tier1Tone} />
        <_Stat label="Tier 2 fallback" value={pct(data.tier2_fallback_rate)} />
        <_Stat label="Brave today" value={String(data.brave_requests_today ?? 0)} />
        <_Stat label="Brave month" value={String(data.brave_requests_month ?? 0)} />
        <_Stat label="Cache hits" value={`${hits}${cacheRate !== null ? ` (${pct(cacheRate)})` : ''}`} />
        <_Stat label="Est. cost (mo)" value={money(data.brave_estimated_cost_month)} />
        <_Stat label="Cap" value={money(cap)} />
        <_Stat label="Remaining" value={money(remaining)} tone={budgetTone} />
      </div>
      <div className="flex items-center justify-between gap-2 pt-1 border-t border-[var(--card-border)]">
        <span data-testid="provider-stack-budget" className={`text-[9px] font-black uppercase tracking-widest ${budgetTone === 'good' ? 'text-emerald-600' : budgetTone === 'warn' ? 'text-amber-600' : 'text-rose-600'}`}>
          {budgetLabel}
        </span>
        <span data-testid="provider-stack-disabled" className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
          EXA: {data.exa_enabled ? 'on' : (data.exa_status === 'quota_cooldown' ? 'disabled (quota-dead)' : 'disabled')} &middot; Tavily: {data.tavily_enabled ? 'on' : 'disabled'}
        </span>
      </div>
      <div className="text-[9px] font-medium tracking-wide text-[var(--muted-text)]">{tier1Note}</div>
    </div>
  );
}
